// SbThemeV2.js — Brand theme config matching styles.scss exactly
// Primary brand color: #0155e7 (blue — 2026 rebrand; previously teal #008080)

import { createGlobalStyle } from 'styled-components';
import albertSans from 'Assets/fonts/albert-sans-latin-var.woff2';
import varela from 'Assets/fonts/varela-latin-400.woff2';

export const SB_COLORS = {
  primary: '#0155e7',       // $primary (brand blue — rebrand 2026; was teal #008080)
  primaryHover: '#0142B5 ',  // darkened blue for hover states (provisional — pending sign-off)
  secondary: '#DCE9FD',     // $secondary (light blue — rebrand; was light teal #DDEAEA)
  tertiary: '#8790A4',      // $tertiary
  light: '#F7F7F7',         // $light
  dark: '#2B3034',          // $dark (body text color)
  gray: '#565E70',          // $gray
  success: '#28a745',       // $success
  info: '#0088CE',          // $info
  danger: '#DC2626',        // $danger
  border: '#D8DAE5',        // $border-color
  borderLight: '#E5E7EB',   // lighter dividers / section borders
  white: '#ffffff',         // pure white backgrounds
  appBg: '#F9F7F3',         // content-area background — warm cream (matches cardBg) for one uniform surface across every screen
  tableHeaderBg: '#EDF1F7',    // table column-header band — light bluish grey (matches the blue theme)
  tableRowHoverBg: '#E5D9C4',  // table row hover 
  cardBg: '#F9F7F3',        // main content-card surface — warm off-white cream from skillsbridge.ai/solutions (single knob across modules)
  hoverBg: 'rgba(1, 85, 231, 0.08)', // $hover-bg (blue — was teal rgba(0,128,128,0.1))
  headerBg: 'rgba(1, 85, 231, 0.5)', // top app-bar wash — #0155e7 @ 50% opacity (rebrand doc §5)

  brandGradient: 'linear-gradient(160deg, #2f74eb 0%, #0155e7 55%, #0142b5 100%)',

  // Login (V2 redesign)
  accentMint: '#06F4CA',        // login CTA + links 
  accentMintHover: '#05D6B2',   // darkened mint for hover
  accentMintText: '#0A2E28',    // dark text on the mint button (legible on mint)
  bridgeLine: '#88F3E0',        // header bridge motif — soft teal used for the self-drawing line + nodes on skillsbridge.ai
  loginGradient: 'linear-gradient(157deg, #0453E7 0%, #1E69E8 42%, #6E7FD6 78%, #C7C3E6 100%)', // full-screen blue→lavender login backdrop

  // Alert color tokens
  successBg: '#F5FBF8',
  successBgStrong: '#E6F4EA',
  successBorder: '#52BD94',
  warningBg: '#FFFAF1',
  warningMessage: '#996A13',
  warningBorder: '#FFB020',
  infoBg: '#EEF4FE', // light blue tint (was cyan/teal-tint #EEFFFC)

  // Table Colors
  tableHoverBg: '#F9FAFB',
  tableRowHover: 'rgba(1, 85, 231, 0.04)', // Table row hover state (blue — was teal)

  // Progress Bar Colors (Role Impact)
  progressBarPeople: '#3F88CE',  // Blue for "People" percentage
  progressBarAI: '#E0E0E0',      // Gray for "AI" percentage

  // Text Colors
  textLight: '#9CA3AF',     // Light gray text for inactive states

  // Button States
  primaryDisabled: '#A8C4F5', // Disabled primary button background (blue-desaturated — was teal #B3D9D9)

  // Status Badge Colors
  statusProcessing: '#F5A623',  // Orange
  statusCompleted: '#4CBF90',   // Green
  statusFailed: '#DC4C4C',      // Red
  statusDefault: '#6C757D',     // Gray

  // Shadow Colors
  focusShadow: 'rgba(1, 85, 231, 0.2)',     // Blue 20% (was teal)
  hoverShadow: 'rgba(1, 85, 231, 0.08)',    // Blue 8% (was teal)
  cardShadow: 'rgba(0, 0, 0, 0.08)',        // Black 8%
  cardShadowMd: 'rgba(0, 0, 0, 0.05)',      // Black 5%


};

// Brand font family — single source shared by Ant Design + styled-components.

export const SB_FONT_FAMILY =
  "'Albert Sans', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

export const SB_HEADING_FONT_FAMILY = `'Varela', ${SB_FONT_FAMILY}`;

export const SbGlobalFonts = createGlobalStyle`
  @font-face {
    font-family: 'Albert Sans';
    src: url(${albertSans}) format('woff2');
    font-weight: 100 900;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Varela';
    src: url(${varela}) format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }

  body {
    font-family: ${SB_FONT_FAMILY};
  }
`;



// Ant Design ConfigProvider theme tokens
// colorPrimary is driven by SB_COLORS.primary (brand blue)
export const SB_ANT_THEME = {
  token: {
    fontFamily: SB_FONT_FAMILY,
    fontSize: 13, 
    colorPrimary: SB_COLORS.primary,
    colorSuccess: SB_COLORS.success,
    colorWarning: SB_COLORS.warningBorder,
    colorError: SB_COLORS.danger,
    colorInfo: SB_COLORS.info,
    borderRadius: 4,
    colorBgContainer: SB_COLORS.white,
    colorText: SB_COLORS.dark,
    colorTextSecondary: SB_COLORS.gray,
    colorBorder: SB_COLORS.border,
    colorTextPlaceholder: SB_COLORS.tertiary,
  },
  components: {
    Button: {
      primaryShadow:'none',
      colorBgContainerDisabled: SB_COLORS.light,
    },
    Layout: {
      headerBg: SB_COLORS.white,
      headerHeight: 60,
      bodyBg: SB_COLORS.light,
    },
    Table: {
      headerBg: SB_COLORS.tableHeaderBg,
      borderColor: SB_COLORS.border,
      headerSortActiveBg: SB_COLORS.secondary,
      rowHoverBg: SB_COLORS.tableRowHoverBg,
    },
    Menu: {
      itemSelectedBg: SB_COLORS.hoverBg,
      itemSelectedColor: SB_COLORS.primary,
    },
    Switch: {
      colorPrimary: SB_COLORS.primary,
      colorPrimaryHover: SB_COLORS.primaryHover,
    },
    Select: {
      optionActiveBg: SB_COLORS.hoverBg,
      optionSelectedBg: 'rgba(1, 85, 231, 0.15)',
      optionSelectedColor: SB_COLORS.dark,
    },
    Tooltip: {
      colorBgSpotlight: 'rgba(0, 0, 0, 0.70)',
      borderRadius: 4,
      fontSize: 12,
      zIndexPopup: 9999,
    },
  },
};

// styled-components ThemeProvider object
export const SB_STYLED_THEME = {
  colors: SB_COLORS,
  typography: { fontFamily: SB_FONT_FAMILY },
};
